import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { MenuItems } from "@common/components/CSV/CSVMenu";
import { mapToCSVData } from "@common/components/CSV/helpers";
import { CatalogContext } from "@common/components/CatalogProvider";
import { FieldValueProvider } from "@common/components/Header/SearchBar/context/fieldValue";
import {
  NotificationContext,
  useErrorNotification,
} from "@common/components/Notification";
import TopBanner from "@common/components/TopBanner";
import { WithGrow } from "@common/components/WithGrow";
import { usePolling } from "@common/hooks";
import { TabValue } from "@pages/Assets/common/FilterColumn/context";
import {
  getAccountDeviceCatalog,
  getCatalog,
  getDeviceAdapters,
  getModelData,
} from "@services/catalog";
import {
  DeviceCatalogMetadata,
  DeviceModelDetail,
} from "@services/catalog/types";

import { useTranslation } from "react-i18next";
import DeviceDialog from "./Dialog";
import DeviceCatalogFilterbar from "./FilterBar";
import { DeviceCatalogList } from "./ListView";
import { getColumns, sortingMapper } from "./ListView/colDefs";
import DeviceTopBanner from "./TopBanner";
import {
  ReducerActions,
  ReducerContext,
  ReducerDispatchContext,
  ReducerState,
  initialState,
  stateReducer,
} from "./context";
import {
  areLocalStorageFiltersApplicable,
  EntityType,
  FiltersTypeImpl,
  useOtherFilters,
  useUserFilter,
} from "@common/utils/filters";
import mappers from "@common/utils/filters/mappers";
import { useLocation } from "react-router-dom";
import { useGlobalListPreferences } from "@common/components/GlobalPreferences/hooks";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import { hasExtendedPropertyMappingLicense } from "@common/utils/user";
import { SpecificPreferencesProvider } from "@common/components/SpecificPreferences";
import { ListViewPreferenceKey } from "@services/userPreferences/types";
import { useSpecificListPreferences } from "@common/components/SpecificPreferences/hooks";
import { sortColumnOrder } from "@common/components/SpecificPreferences/utils";
import { useCustomSearchParams } from "@services/common/hooks/useSearchParams";

interface DeviceCatalogProps {
  accountId?: string;
  activeOnly?: boolean;
}

const DeviceCatalogComponent = (props: DeviceCatalogProps) => {
  const { accountId, activeOnly } = props;
  const [catalogFilters] = useUserFilter("deviceCatalogFilters");
  const [state, dispatch] = useReducer(
    (state: ReducerState, action: ReducerActions): ReducerState =>
      stateReducer(state, action),
    initialState
  );
  const [data, setData] = useState<DeviceModelDetail[]>();
  const [metaData, setMetaData] = useState<DeviceCatalogMetadata>();
  const [sort, setSort] = useState<Sort<DeviceModelDetail>>({
    column: "manufacturer",
    direction: "asc",
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [{ pageSize }] = useGlobalListPreferences();
  const [filters, setFilters] = useOtherFilters(FiltersTypeImpl.DeviceCatalog);
  const [active, setActive] = useState<boolean>(activeOnly ?? true);
  const [openDeviceDialog, setOpenDeviceDialog] = useState<boolean>(false);
  const { dispatchNotification } = useContext(NotificationContext);
  const dispatchError = useErrorNotification();
  const catalog = useContext(CatalogContext);
  const [searchParams] = useCustomSearchParams();
  const [, , allAccounts] = useUserSelectedAccounts();
  const [{ columnOrder }] = useSpecificListPreferences();

  const canAccessExtendedPropertyMapping = hasExtendedPropertyMappingLicense(
    allAccounts.find((a) => a.id === accountId)
  );

  const { t } = useTranslation();

  const refetch = useCallback(async () => {
    const filters = mappers.DeviceCatalog.fromSearchParams.toSL(searchParams);
    try {
      setData(undefined);
      const deviceCatalog = accountId
        ? await getAccountDeviceCatalog(
            accountId,
            active,
            undefined,
            pageSize,
            `${sortingMapper(sort.column)} ${sort.direction}`,
            (page - 1) * pageSize,
            filters
          )
        : await getCatalog(
            active,
            undefined,
            pageSize,
            `${sortingMapper(sort.column)} ${sort.direction}`,
            (page - 1) * pageSize,
            filters
          );
      setData(deviceCatalog?.deviceModelDetails?.list ?? []);
      setMetaData(deviceCatalog?.deviceCatalogMetadata);
      setTotal(deviceCatalog?.deviceModelDetails?.total ?? 0);
    } catch (error) {
      dispatchError(error);
    }
  }, [accountId, page, sort, filters, active, pageSize]);

  const poll = usePolling(refetch, 60e3);

  const getGlobalDeviceModel = useCallback(
    async (model: string | undefined) => {
      if (model) {
        try {
          const deviceModel = await getModelData(model);
          dispatch({ globalDevice: deviceModel });
        } catch (error) {
          dispatchError(error);
        }
      }
    },
    [accountId]
  );

  const fetchAdapters = useCallback(
    async (model: string | undefined) => {
      const response = await getDeviceAdapters(model, accountId);
      dispatch({ deviceAdapters: response });
    },
    [accountId]
  );

  useEffect(() => {
    if (
      metaData?.importStatus &&
      metaData?.importStatus.toLowerCase().startsWith("importing")
    ) {
      dispatchNotification({
        title: t("Import Status"),
        severity: "success",
        message: `
        ${metaData.importStatus}.
        ${t(
          "The file is now uploaded, it may take several minutes for the system to incorporate all changes from this new catalog"
        )}`,
      });
      poll.start();
    } else if (
      metaData?.importStatus &&
      metaData?.importStatus.toLowerCase().startsWith("failed")
    ) {
      dispatchNotification({
        title: t("Import Status"),
        message: metaData.importStatus,
        severity: "error",
      });
      if (poll.isPolling) {
        setTimeout(() => {
          poll.stop();
        }, 30e3);
      }
    } else {
      if (poll.isPolling) {
        setTimeout(() => {
          poll.stop();
        }, 30e3);
      }
    }
  }, [metaData?.importStatus]);

  useEffect(() => {
    refetch();
  }, [page, sort, filters, active, pageSize]);

  const onModelClick = (row: DeviceModelDetail) => {
    if (accountId) dispatch({ accountId, canAccessExtendedPropertyMapping });
    dispatch({ device: row });
    if (accountId) getGlobalDeviceModel(row?.model);
    else dispatch({ globalDevice: row });
    fetchAdapters(row?.model);

    setOpenDeviceDialog(true);
  };

  const getDataForCSV = async (item: MenuItems) => {
    const filters = mappers.DeviceCatalog.fromSearchParams.toSL(searchParams);
    const deviceCatalog = accountId
      ? await getAccountDeviceCatalog(
          accountId,
          active,
          undefined,
          undefined,
          `${sort.column} ${sort.direction}`,
          undefined,
          filters
        )
      : await getCatalog(
          active,
          undefined,
          undefined,
          `${sort.column} ${sort.direction}`,
          undefined,
          filters
        );

    const exportTab =
      item === MenuItems.ListAsDisplayed ? TabValue.Info : TabValue.Export;

    let exportColumns = getColumns(onModelClick, catalog).filter(
      (c) => c.tab && c.tab.indexOf(exportTab) !== -1
    );
    if (item === MenuItems.ListAsDisplayed) {
      exportColumns = sortColumnOrder(exportColumns, columnOrder);
    }

    const fileName =
      item === MenuItems.ListAsDisplayed ? "catalog" : "catalogsraw";

    return mapToCSVData(
      deviceCatalog?.deviceModelDetails?.list as DeviceModelDetail[],
      exportColumns,
      fileName,
      undefined
    );
  };

  return (
    <>
      <WithGrow
        direction="column"
        fixed={
          <TopBanner title={t("Device Catalog")}>
            <DeviceTopBanner
              isLoading={data === undefined}
              metaData={metaData}
              isPolling={poll.isPolling}
            />
          </TopBanner>
        }
      >
        <FieldValueProvider>
          <WithGrow
            direction="column"
            fixed={
              <DeviceCatalogFilterbar
                total={total}
                loading={data === undefined}
                filters={filters}
                setFilters={(filter) => {
                  setPage(1);
                  setFilters(filter);
                }}
                active={active}
                setActive={setActive}
                refetch={refetch}
                accountId={accountId}
                csvExportProps={{
                  getData: getDataForCSV,
                  availableMenuItems: [
                    MenuItems.ListAsDisplayed,
                    MenuItems.RawData,
                  ],
                }}
              />
            }
          >
            <DeviceCatalogList
              onModelClick={onModelClick}
              loading={data === undefined}
              data={data ?? []}
              total={total}
              pageSize={pageSize}
              page={page}
              setPage={setPage}
              sort={sort}
              setSort={setSort}
            />
          </WithGrow>
        </FieldValueProvider>
      </WithGrow>
      <ReducerContext.Provider value={state}>
        <ReducerDispatchContext.Provider value={dispatch}>
          <DeviceDialog
            open={openDeviceDialog}
            onClose={() => setOpenDeviceDialog(false)}
            refetch={refetch}
          />
        </ReducerDispatchContext.Provider>
      </ReducerContext.Provider>
    </>
  );
};

export const DeviceCatalog = (props: DeviceCatalogProps) => {
  const [filters, setFilters] = useOtherFilters(FiltersTypeImpl.DeviceCatalog);
  const [catalogFilters] = useUserFilter("deviceCatalogFilters");
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const location = useLocation();
  useEffect(() => {
    setHasLoaded(true);
    if (
      filters.length === 0 &&
      areLocalStorageFiltersApplicable(
        catalogFilters,
        location.state,
        EntityType.DEVICE_CATALOG
      )
    ) {
      setFilters(catalogFilters ?? []);
    }
  }, [location.search]);

  const isFiltersReady =
    hasLoaded && (filters.length > 0 || catalogFilters?.length === 0);

  if (!isFiltersReady) return <></>;
  return (
    <SpecificPreferencesProvider
      assetKey={ListViewPreferenceKey.DEVICE_CATALOG}
    >
      <DeviceCatalogComponent {...props} />
    </SpecificPreferencesProvider>
  );
};
